import logger from '../../Utils/logger';
import React, { createContext, useContext, useReducer, useMemo, useCallback } from 'react';
import { guestsReducer, guestActions } from './GuestsReducer';
import {
  selectActiveGuest,
  selectSelectedServices,
  selectServiceCount,
  selectCartTotal,
  selectMemberCount,
  selectActiveGuestServiceCount,
  selectActiveGuestSubtotal,
  selectGroupNextEnabled,
  selectSelectedServicesID,
  selectSelectedMemberIndex,
} from './guestSelectors';
import { getSessionStorageValue, setSessionStorageValue, STORAGE_KEYS } from '../../Utils/SessionStorage';
import { createInitialGuests, normalizeMembersList, PRIMARY_GUEST_ID } from '../../Utils/guestMembers';
import { useWebSettings } from '../WebSettings/WebSettingsContext';

const GuestsContext = createContext();

export const GuestsProvider = ({ children, isSingleBooking, isGroupBooking }) => {
  const { maxServiceCount } = useWebSettings();
  // Initialize members from sessionStorage or create defaults
  const [members, dispatch] = useReducer(
    guestsReducer,
    null,
    () => {
      const stored = getSessionStorageValue(STORAGE_KEYS.UPDATED_MEMBERS);
      const appt = getSessionStorageValue(STORAGE_KEYS.APPOINTMENT_TYPE);

      let list = stored && Array.isArray(stored) && stored.length > 0
        ? normalizeMembersList(stored)
        : createInitialGuests();

      // Single booking mode: filter to primary guest only
      if (Number(appt) === 1 && list.length > 1) {
        const primary = list.find((m) => m.isPrimary) || list[0];
        list = normalizeMembersList([
          { ...primary, isPrimary: true, services: primary.services || [] },
        ]);
      }

      return list;
    }
  );

  // Sync members to sessionStorage whenever they change (member flow only)
  // Data persists only for current tab session
  React.useEffect(() => {
    if (!isSingleBooking && !isGroupBooking) return;
    try {
      setSessionStorageValue(STORAGE_KEYS.UPDATED_MEMBERS, members);
    } catch (e) {
      logger.error('Failed to sync members to sessionStorage:', e);
    }
  }, [members, isSingleBooking, isGroupBooking]);

  // Active guest ID state
  const [activeGuestId, setActiveGuestIdState] = React.useState(
    () => getSessionStorageValue(STORAGE_KEYS.ACTIVE_GUEST_ID) || PRIMARY_GUEST_ID
  );

  const setActiveGuestId = useCallback((id) => {
    setActiveGuestIdState(id);
    setSessionStorageValue(STORAGE_KEYS.ACTIVE_GUEST_ID, id);
  }, []);

  const [primaryCustomer, setPrimaryCustomerState] = React.useState(
    () => getSessionStorageValue(STORAGE_KEYS.PRIMARY_CUSTOMER) || null
  );

  const setPrimaryCustomer = useCallback((customer) => {
    setPrimaryCustomerState(customer);
    setSessionStorageValue(STORAGE_KEYS.PRIMARY_CUSTOMER, customer);
  }, []);

  // Keep updated_Members[0].name aligned with primaryCustomer
  React.useEffect(() => {
    if (!primaryCustomer || members.length === 0) return;
    if (members[0].name === primaryCustomer) return;
    dispatch(guestActions.updateMember(members[0].id, { name: primaryCustomer }));
  }, [primaryCustomer, members]);

  // Filter to single booking when appointment type becomes 1
  React.useEffect(() => {
    if (!isSingleBooking) return;
    if (members.length <= 1) return;
    dispatch(guestActions.filterToSingleBooking());
  }, [isSingleBooking, members.length]);

  // Action wrappers (dispatch helpers)
  const addGuest = useCallback(() => {
    const action = guestActions.addGuest();
    dispatch(action);
    // After dispatch, find the new guest ID and set as active
    // This happens synchronously, so we can safely update activeGuestId
    // Note: The reducer returns the new state; we'll get the new guest's ID
    // from the next effect or from the caller's response.
  }, []);

  const removeGuest = useCallback((guestId) => {
    const wasActive = activeGuestId === guestId;
    dispatch(guestActions.removeGuest(guestId));
    if (wasActive) {
      setActiveGuestId(PRIMARY_GUEST_ID);
    }
  }, [activeGuestId, setActiveGuestId]);

  const updateMember = useCallback((memberId, updates) => {
    dispatch(guestActions.updateMember(memberId, updates));
  }, []);

  const updateMemberServices = useCallback((memberId, services) => {
    dispatch(guestActions.updateMemberServices(memberId, services));
  }, []);

  const addServiceToGuest = useCallback((memberId, service) => {
    dispatch(guestActions.addServiceToGuest(memberId, service, maxServiceCount));
  }, [maxServiceCount]);

  const removeServiceFromGuest = useCallback((memberId, serviceId, count) => {
    dispatch(guestActions.removeServiceFromGuest(memberId, serviceId, count));
  }, []);

  // Flexible update for complex member mutations (used for addon selection, etc.)
  const updateMembers = useCallback((updater) => {
    dispatch(guestActions.normalizeMembers(updater(members)));
  }, [members]);

  const resetMembers = useCallback(() => {
    dispatch(guestActions.resetMembers());
    setActiveGuestId(PRIMARY_GUEST_ID);
  }, [setActiveGuestId]);

  const normalizeMembers = useCallback((list) => {
    dispatch(guestActions.normalizeMembers(list));
  }, []);

  // Memoized selectors (use useCallback where they accept parameters)
  const activeGuest = useMemo(
    () => selectActiveGuest(members, activeGuestId),
    [members, activeGuestId]
  );

  const selectedServices = useMemo(
    () => selectSelectedServices(members),
    [members]
  );

  const serviceCount = useMemo(
    () => selectServiceCount(members),
    [members]
  );

  const activeGuestServiceCount = useMemo(
    () => selectActiveGuestServiceCount(members, activeGuestId),
    [members, activeGuestId]
  );

  const memberCount = useMemo(
    () => selectMemberCount(members),
    [members]
  );

  const selectedMemberIndex = useMemo(
    () => selectSelectedMemberIndex(members, activeGuestId),
    [members, activeGuestId]
  );

  const selectedServicesID = useMemo(
    () => selectSelectedServicesID(members),
    [members]
  );

  // Memoized selector that requires external dependencies (membership, selectors that take params)
  const selectCartTotalMemoized = useCallback(
    (membership) => selectCartTotal(members, membership),
    [members]
  );

  const selectActiveGuestSubtotalMemoized = useCallback(
    (membership) => selectActiveGuestSubtotal(members, activeGuestId, membership),
    [members, activeGuestId]
  );

  const selectGroupNextEnabledMemoized = useCallback(
    () => selectGroupNextEnabled(members, isSingleBooking, isGroupBooking),
    [members, isSingleBooking, isGroupBooking]
  );

  const value = useMemo(
    () => ({
      // State
      members,
      activeGuestId,
      primaryCustomer,

      // Setters
      setActiveGuestId,
      setPrimaryCustomer,
      updateMember,
      updateMembers,
      updateMemberServices,

      // Actions (dispatch wrappers)
      addGuest,
      removeGuest,
      addServiceToGuest,
      removeServiceFromGuest,
      resetMembers,
      normalizeMembers,

      // Selectors
      activeGuest,
      selectedServices,
      serviceCount,
      activeGuestServiceCount,
      memberCount,
      selectedMemberIndex,
      selectedServicesID,

      // Selector factories (call with params)
      selectCartTotal: selectCartTotalMemoized,
      selectActiveGuestSubtotal: selectActiveGuestSubtotalMemoized,
      selectGroupNextEnabled: selectGroupNextEnabledMemoized,
    }),
    [
      members,
      activeGuestId,
      primaryCustomer,
      setActiveGuestId,
      setPrimaryCustomer,
      updateMember,
      updateMembers,
      updateMemberServices,
      addGuest,
      removeGuest,
      addServiceToGuest,
      removeServiceFromGuest,
      resetMembers,
      normalizeMembers,
      activeGuest,
      selectedServices,
      serviceCount,
      activeGuestServiceCount,
      memberCount,
      selectedMemberIndex,
      selectedServicesID,
      selectCartTotalMemoized,
      selectActiveGuestSubtotalMemoized,
      selectGroupNextEnabledMemoized,
    ]
  );

  return (
    <GuestsContext.Provider value={value}>
      {children}
    </GuestsContext.Provider>
  );
};

export const useGuests = () => {
  const context = useContext(GuestsContext);
  if (!context) {
    throw new Error('useGuests must be used within GuestsProvider');
  }
  return context;
};

export default GuestsContext;
